import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Table, { ColumnsType } from 'antd/lib/table';
import { useState } from 'react';
import FileOutlined from '@ant-design/icons/FileOutlined';
import { PjmGeneratorInfo } from 'src/domain/pjm/dto/PjmGeneratorInfo';
import { LocationGeneratorModal } from './LocationGeneratorModal';
import _ from 'lodash';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import Flex from 'antd/lib/flex';
import { ActionList } from 'src/components/table/actionList/actionList';
import ExclamationCircleOutlined from '@ant-design/icons/ExclamationCircleOutlined';
import modal from 'antd/lib/modal';
import * as uuid from 'uuid';
import { Tooltip } from 'antd';
import { MAX_GENERATORS_AMOUNT } from 'src/domain/pjm';

interface LocationGeneratorInfoTableProps {
    value: Array<PjmGeneratorInfo & { _id: string }>;
    onHide: () => void;
    onChange: (value: Array<PjmGeneratorInfo & { _id: string }>) => void;
}

export function LocationGeneratorInfoTable({ value, onHide, onChange }: LocationGeneratorInfoTableProps) {
    let records = value;
    const [showModal, setShowModal] = useState<(PjmGeneratorInfo & { _id: string }) | null>(null);

    function onGeneratorInfoChange(data: PjmGeneratorInfo & { _id: string }) {
        // here we could receive updated record or new record
        const isNew = records.filter(it => it._id === data._id).length === 0;
        if (isNew) {
            records.push(data);
        } else {
            for (let i = 0; i < records.length; i++) {
                if (records[i]._id === data._id) {
                    records[i] = { ...data };
                }
            }
        }

        onChange(records);
        setShowModal(null);
    }

    function showAddGeneratorModal() {
        setShowModal({
            _id: uuid.v4(),
            nonRetailBtmg: false,
            maxOutput: 0,
            backupGeneratorOnly: false,
        });
    }

    function onModalClose() {
        setShowModal(null);
    }

    function deleteWithConfirmation(index: number) {
        modal.confirm({
            icon: <ExclamationCircleOutlined />,
            content: `Deleting generator details. Are you sure?`,
            onOk() {
                records.splice(index, 1);
                onChange(records);
            },
            onCancel() {},
        });
    }

    const actions = [
        {
            title: 'Edit',
            onClick:
                ([record, index]: [record: PjmGeneratorInfo & { _id: string }, index: number]) =>
                () =>
                    setShowModal(record),
        },
        {
            title: 'Delete',
            onClick:
                ([record, index]: [record: PjmGeneratorInfo & { _id: string }, index: number]) =>
                () =>
                    deleteWithConfirmation(index),
        },
    ];

    const columns: ColumnsType<PjmGeneratorInfo & { _id: string }> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            fixed: 'left',
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            fixed: 'left',
        },
        {
            title: 'Non-Retail BTMG',
            dataIndex: 'nonRetailBtmg',
            key: 'nonRetailBtmg',
            render: (text, record) => (text ? 'Yes' : 'No'),
        },
        {
            title: 'Max Output (kW)',
            dataIndex: 'maxOutput',
            key: 'maxOutput',
        },
        {
            title: 'Nameplate (kW)',
            dataIndex: 'nameplate',
            key: 'nameplate',
        },
        {
            title: 'Backup Generator Only',
            dataIndex: 'backupGeneratorOnly',
            key: 'backupGeneratorOnly',
            render: (text, record) => (text ? 'Yes' : 'No'),
        },
        {
            title: 'Generator Type',
            dataIndex: 'generatorType',
            key: 'generatorType',
        },
        {
            title: 'Fuel Type',
            dataIndex: 'fuelType',
            key: 'fuelType',
        },
        {
            title: 'Vintage',
            dataIndex: 'vintage',
            key: 'vintage',
        },
        {
            title: 'Retrofit Year',
            dataIndex: 'retrofitYear',
            key: 'retrofitYear',
        },
        {
            title: 'Permit Status',
            dataIndex: 'permitStatus',
            key: 'permitStatus',
        },
        {
            title: 'Permit Type',
            dataIndex: 'permitType',
            key: 'permitType',
        },
        {
            title: '202c Specified Resource?',
            dataIndex: 'order202c',
            key: 'order202c',
            render: (text, record) => (text ? 'Yes' : 'No'),
        },
        {
            title: 'Note',
            dataIndex: 'note',
            key: 'note',
            render: (text, record) => (_.isEmpty(text) ? 'No' : 'Yes'),
        },
        {
            title: 'Agreement Type',
            dataIndex: 'agreementType',
            key: 'agreementType',
        },
        {
            title: 'Injection Rights (MW)',
            dataIndex: 'injectionRight',
            key: 'injectionRight',
        },
        {
            title: 'Actions',
            key: 'actions',
            fixed: 'right',
            minWidth: 60,
            render: (text, record, index) => {
                return <ActionList actions={actions} item={[record, index]} />;
            },
        },
    ];

    const isMaxLimit = records.length >= MAX_GENERATORS_AMOUNT;

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <Flex style={{ width: '100%' }} justify={'space-between'} align={'center'}>
                {isMaxLimit ? (
                    <Tooltip title={`Cannot have more than ${MAX_GENERATORS_AMOUNT} generators`}>
                        <Button onClick={showAddGeneratorModal} icon={<FileOutlined />} disabled={true}>
                            Add Generator
                        </Button>
                    </Tooltip>
                ) : (
                    <Button onClick={showAddGeneratorModal} icon={<FileOutlined />}>
                        Add Generator
                    </Button>
                )}
                <Button onClick={() => onHide()} icon={<CloseOutlined />} type="text">
                    Hide
                </Button>
            </Flex>

            <Table dataSource={records} columns={columns} pagination={false} rowKey="_id" tableLayout={'auto'} scroll={{ x: 'scroll' }} />

            {!!showModal && (
                <LocationGeneratorModal
                    key="pjm-location-generator-modal"
                    onClose={onModalClose}
                    onChange={onGeneratorInfoChange}
                    value={showModal}
                />
            )}
        </Space>
    );
}
